import {readTreasuryAuthorityResponse} from '../assembly-governance.js';
import {expenseActions} from '../expense-requests.js';
export class TreasuryExpenseResponseContractError extends Error {
  constructor(operation, detail) {
    super(`Invalid Treasury expense ${operation} response: ${detail}`);
    this.name = 'TreasuryExpenseResponseContractError';
    this.code = 'INVALID_API_RESPONSE';
  }
}

const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value)
  && !Object.keys(value).some(key => /^(?:password|passwordHash|token|accessToken|refreshToken|secret|authorization|privateKey|mnemonic|seedPhrase)$/i.test(key));
const text = value => typeof value === 'string' && value.length > 0;
const date = value => value instanceof Date ? Number.isFinite(value.getTime()) : typeof value === 'string' && Number.isFinite(Date.parse(value));
const money = value => Number.isFinite(value) && value >= 0 && Number.isSafeInteger(Math.round(value * 100)) && Math.abs(value * 100 - Math.round(value * 100)) < 0.0001;
const nullableMoney = value => value === null || money(value);
const currency = value => typeof value === 'string' && /^[A-Z]{3}$/.test(value);
const optional = (value, keys, guard) => keys.split(' ').every(key => value[key] == null || guard(value[key]));
const unique = rows => rows.every(row => isRecord(row) && text(row.id)) && new Set(rows.map(row => row.id.toLowerCase())).size === rows.length;
const statuses = ['submitted', 'under_review', 'more_information_needed', 'approved', 'payment_in_progress', 'paid', 'declined', 'withdrawn', 'awaiting_settlement', 'settlement_under_review', 'settlement_information_needed', 'awaiting_return', 'return_awaiting_confirmation', 'awaiting_reconciliation', 'settled'];
const amendmentSnapshot = row => isRecord(row) && ['title', 'description', 'fundId', 'fundName'].every(key => typeof row[key] === 'string') && (row.categoryId === null || text(row.categoryId)) && money(row.requestedAmount) && currency(row.currency);
const historyDetails = row => isRecord(row)
  && optional(row, 'requestedAmount approvedAmount payableAmount claimedAmount authorisedAmount releasedAmount paidAmount expenditureSubmitted expenditureRecognised returnDue declaredReturn returnReceived expenditure returned', money)
  && optional(row, 'budgetAllocationId receiptId transactionId', text)
  && optional(row, 'approvalAuthority', value => ['assembly_decision', 'delegated_authority'].includes(value))
  && optional(row, 'previousPaymentAuthoriser', text)
  && optional(row, 'approvalReference budgetAllocationTitle paymentReference', value => typeof value === 'string')
  && optional(row, 'separatePaymentAuthoriser', value => typeof value === 'boolean')
  && optional(row, 'returnDate', date)
  && optional(row, 'amendment', value => isRecord(value) && amendmentSnapshot(value.before) && amendmentSnapshot(value.after))
  && optional(row, 'receiptIds', rows => Array.isArray(rows) && rows.length <= 10 && rows.every(text) && new Set(rows).size === rows.length);
const receiptType = value => ['image/jpeg', 'image/png', 'application/pdf'].includes(value);
const fail = (operation, detail) => { throw new TreasuryExpenseResponseContractError(operation, detail); };

const isReceiptReference = receipt => isRecord(receipt)
  && text(receipt.id) && text(receipt.name) && receiptType(receipt.contentType)
  && Number.isInteger(receipt.size) && receipt.size > 0 && receipt.size <= 5 * 1024 * 1024
  && date(receipt.uploadedAt);

const isExpense = row => isRecord(row)
  && ['id', 'communityId', 'requesterId', 'requesterName', 'fundId', 'fundName', 'currency', 'title'].every(key => text(row[key]))
  && optional(row, 'categoryId', text)
  && typeof row.description === 'string'
  && typeof row.budgetName === 'string' && typeof row.budgetAllocationTitle === 'string'
  && ['reimbursement', 'preapproval', 'advance'].includes(row.kind)
  && currency(row.currency) && statuses.includes(row.status)
  && (row.budgetId === null || text(row.budgetId)) && (row.budgetAllocationId === null || text(row.budgetAllocationId))
  && (row.source === null || isRecord(row.source) && ['activity', 'session', 'event'].includes(row.source.type) && text(row.source.id) && text(row.source.title))
  && money(row.requestedAmount) && nullableMoney(row.approvedAmount) && nullableMoney(row.claimedAmount)
  && money(row.committedAmount) && nullableMoney(row.payableAmount) && nullableMoney(row.paidAmount)
  && optional(row, 'approvalReference', value => typeof value === 'string')
  && optional(row, 'approvalAuthority', value => ['assembly_decision', 'delegated_authority'].includes(value))
  && typeof row.paymentReference === 'string' && (row.transactionId === null || text(row.transactionId))
  && Number.isSafeInteger(row.revision) && row.revision >= 0
  && (row.replayed === undefined || typeof row.replayed === 'boolean')
  && (row.paidAt === null || date(row.paidAt))
  && typeof row.separatePaymentAuthoriser === 'boolean' && text(row.paymentAuthorisationNotice)
  && Array.isArray(row.receipts) && row.receipts.length <= 10 && unique(row.receipts) && row.receipts.every(isReceiptReference)
  && Array.isArray(row.history) && row.history.every(entry => isRecord(entry)
    && text(entry.action) && text(entry.actor) && optional(entry, 'actorName', value => typeof value === 'string') && date(entry.at) && typeof entry.note === 'string' && (entry.details === undefined || historyDetails(entry.details))
    && optional(entry, 'fromStatus toStatus', value => statuses.includes(value)))
  && date(row.createdAt) && date(row.updatedAt)
  && Array.isArray(row.allowedActions) && new Set(row.allowedActions).size === row.allowedActions.length && row.allowedActions.every(action => [...expenseActions, 'receipt'].includes(action))
  && (row.kind !== 'advance' || isRecord(row.advance)
    && ['expenditureSubmitted', 'expenditureConfirmed', 'outstandingAmount', 'returnDue', 'returnedAmount'].every(key => nullableMoney(row.advance[key]))
    && money(row.advance.outstandingAmount) && money(row.advance.returnedAmount)
    && ['returnDeclaredAt', 'returnedAt', 'reviewedAt', 'completedAt'].every(key => row.advance[key] === undefined || row.advance[key] === null || date(row.advance[key]))
    && ['returnDeclarationReference', 'returnReference'].every(key => row.advance[key] === undefined || typeof row.advance[key] === 'string')
    && (row.advance.returnTransactionId === undefined || row.advance.returnTransactionId === null || text(row.advance.returnTransactionId)));

export const readTreasuryExpenseResponse = (payload, requestedId) => {
  if (!isRecord(payload) || !isExpense(payload.data)) fail('detail', 'expected the permission-filtered expense projection.');
  if (requestedId && payload.data.id.toLowerCase() !== requestedId.toLowerCase()) fail('detail', 'expected the requested expense.');
  return payload;
};

export const readTreasuryExpenseRecoveryContextResponse = payload => {
  if (!isRecord(payload) || !isRecord(payload.data)
    || typeof payload.data.scope !== 'string'
    || !/^[a-f0-9]{24}_[a-f0-9]{24}$/i.test(payload.data.scope)) {
    fail('recovery context', 'expected the authenticated user and community scope.');
  }
  return payload.data.scope;
};

export const readTreasuryExpenseRecoveryResponse = payload => {
  if (!isRecord(payload) || !['recorded', 'not_recorded'].includes(payload.outcome)
    || !Object.hasOwn(payload, 'data')
    || (payload.outcome === 'not_recorded' && payload.data !== null)
    || (payload.outcome === 'recorded' && payload.data !== null
      && !isExpense(payload.data))) {
    fail('recovery result', 'expected a recorded or closed attempt with its current authorised expense projection.');
  }
  return payload;
};

export const readTreasuryExpenseReceiptResponse = (payload, receipt) => {
  if (!isReceiptReference(receipt) || payload == null
    || typeof payload.size !== 'number' || payload.size !== receipt.size
    || payload.type !== receipt.contentType || typeof payload.arrayBuffer !== 'function') {
    fail('receipt download', 'expected the private JPEG, PNG or PDF bytes with the metadata declared by the authorised expense.');
  }
  return payload;
};

export const readTreasuryExpenseReceiptReference = receipt => {
  if (!isReceiptReference(receipt)) {
    fail('receipt reference', 'expected an authorised JPEG, PNG or PDF receipt and its complete metadata.');
  }
  return receipt;
};

export const readTreasuryExpenseListResponse = payload => {
  if (payload?.authorityOptions !== undefined) readTreasuryAuthorityResponse({data: payload.authorityOptions});
  if (!isRecord(payload) || !Array.isArray(payload.data) || payload.data.length > 50 || !unique(payload.data) || !payload.data.every(isExpense)
    || typeof payload.canReview !== 'boolean'
    || !Array.isArray(payload.allocations) || !unique(payload.allocations) || !payload.allocations.every(row => isRecord(row)
      && ['id', 'fundId', 'budgetId', 'budgetName', 'title', 'currency'].every(key => text(row[key])) && typeof row.periodLabel === 'string'
      && currency(row.currency) && ['allocatedAmount', 'actualAmount', 'committedAmount', 'advanceOutstandingAmount', 'uncommittedAmount'].every(key => money(row[key])))
    || (payload.categories !== undefined && (!Array.isArray(payload.categories) || !unique(payload.categories) || !payload.categories.every(row => isRecord(row)
      && ['id', 'title', 'budgetName'].every(key => text(row[key])) && typeof row.periodLabel === 'string'
      && currency(row.currency) && typeof row.supportsAdvances === 'boolean'
      && Object.keys(row).every(key => ['id', 'title', 'budgetName', 'periodLabel', 'currency', 'supportsAdvances'].includes(key)))))
    || !(payload.nextCursor === null || typeof payload.nextCursor === 'string' && /^[a-f\d]{24}$/i.test(payload.nextCursor))
    || !Array.isArray(payload.funds) || !unique(payload.funds) || !payload.funds.every(fund => isRecord(fund)
      && text(fund.id) && text(fund.fundName) && currency(fund.currency) && typeof fund.supportsAdvances === 'boolean'
      && (fund.balance === undefined || money(fund.balance))
      && (fund.committedAmount === undefined || money(fund.committedAmount))
      && (fund.advanceOutstandingAmount === undefined || money(fund.advanceOutstandingAmount))
      && (fund.lastUpdated === undefined || fund.lastUpdated === null || date(fund.lastUpdated)))) {
    fail('list', 'expected scoped expense rows, permission-filtered allocations and eligible funds.');
  }
  return payload;
};
